import { html, LitElement, nothing, type TemplateResult } from "lit";
import { empty, sort, sortKey, unique } from "@uri/gamla";
import type {
  Attachment,
  Credentials,
  DecipheredMessage,
} from "../../protocol/src/clientApi.ts";
import {
  createConversation,
  downloadAttachment,
  generateTransferUrl,
  parseChatWithUrl,
  resolveHandle,
  sendMessageWithKey,
  uploadAttachment,
} from "../../protocol/src/clientApi.ts";
import { importIdentity, saveCredentials } from "../core/credentials.ts";
import { accessAdminDb, accessDb } from "../core/instant-client.ts";
import {
  compactPublicKey,
  type Conversation,
  createTypingNotifier,
  nextConversationKey,
  subscribeConversationKey,
  subscribeConversations,
  subscribeDecryptedMessages,
  subscribeIdentityDetailsMap,
  subscribeTypingStates,
  subscribeUserName,
} from "../core/subscriptions.ts";
import { subscribeIsMobile } from "../core/responsive.ts";
import {
  type EphemeralStreamEvent,
  subscribeEphemeralStreams,
} from "../core/room.ts";
import {
  createVoiceCall,
  type VoiceCallController,
} from "../core/voice-call.ts";
import "./chat-box.ts";
import "./user-profile-popup.ts";
import "./chat-avatar.ts";
import { avatarColor, defaultPrimary } from "./design.ts";
import {
  collectIdentityKeys,
  copyToClipboard,
  type IdentityDetails,
  mergeIdentityDetails,
  type Participant,
  resolveConversationDisplayName,
} from "./utils.ts";
import type {
  AbstracChatMessage,
  ActiveProgress,
  ActiveSpinner,
  ActiveStream,
  CustomColors,
  EditHistoryEntry,
  Reaction,
} from "./types.ts";
import {
  isPast,
  latestTimestamp,
  standaloneSpinnerEntries,
} from "./transient-elements.ts";

type TextOrEditMessage = DecipheredMessage & { type: "text" | "edit" };

const isTextOrEdit = (m: DecipheredMessage): m is TextOrEditMessage =>
  m.type === "text" || m.type === "edit";

const hasAttachments = (
  msg: DecipheredMessage,
): msg is DecipheredMessage & { attachments?: Attachment[] } =>
  msg.type === "text" || msg.type === "edit";

const editsForMessage = (edits: TextOrEditMessage[]) => (msgId: string) =>
  sortKey((e: TextOrEditMessage) => e.timestamp)(
    edits.filter((e) => e.type === "edit" && e.editOf === msgId),
  );

const buildEditHistory =
  (edits: TextOrEditMessage[]) =>
  (original: TextOrEditMessage): EditHistoryEntry[] => [
    ...editsForMessage(edits)(original.id).slice(0, -1).map((e) => ({
      text: e.text,
      timestamp: e.timestamp,
      attachments: e.attachments,
    })),
    {
      text: original.text,
      timestamp: original.timestamp,
      attachments: original.attachments,
    },
  ];

const applyLatestEdit =
  (edits: TextOrEditMessage[]) =>
  (original: DecipheredMessage): DecipheredMessage => {
    if (original.type !== "text") return original;
    const msgEdits = editsForMessage(edits)(original.id);
    if (!msgEdits.length) return original;
    const latest = msgEdits[msgEdits.length - 1];
    return { ...original, text: latest.text, attachments: latest.attachments };
  };

const foldEdits = (messages: DecipheredMessage[]) => {
  const edits = messages.filter((m): m is TextOrEditMessage =>
    m.type === "edit"
  );
  return messages.filter((m) => m.type === "text").map((original) => {
    const msg = applyLatestEdit(edits)(original);
    return {
      msg,
      editHistory: original.type === "text" &&
          edits.some((e) => e.type === "edit" && e.editOf === original.id)
        ? buildEditHistory(edits)(original as TextOrEditMessage)
        : undefined,
    };
  });
};

const resolveName = (details: IdentityDetails) => (key: string): string =>
  details[key]?.name ?? compactPublicKey(key);

const resolveParticipantName = (p: Participant, details: IdentityDetails) =>
  p.name || details[p.publicSignKey]?.name || compactPublicKey(p.publicSignKey);

const resolveParticipantAvatar = (p: Participant, details: IdentityDetails) =>
  p.avatar || details[p.publicSignKey]?.avatar || "";

const participantsOverlayStyle =
  "position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:1000";

const participantsPopupStyle = (isDark: boolean) =>
  `background:${
    isDark ? "#1a1a1a" : "#fff"
  };border-radius:16px;padding:24px;min-width:300px;max-width:400px;width:90%;max-height:80vh;border:1px solid ${
    isDark ? "#2a2a2a" : "#e5e7eb"
  };box-shadow:${
    isDark ? "0 8px 24px #0008" : "0 8px 24px #0002"
  };display:flex;flex-direction:column;gap:16px;box-sizing:border-box`;

const participantsListStyle =
  "display:flex;flex-direction:column;gap:12px;overflow-y:auto;width:100%";

const participantRowStyle = (isDark: boolean) =>
  `display:flex;align-items:center;justify-content:space-between;gap:12px;padding:8px 12px;border-radius:12px;cursor:pointer;transition:background 0.2s;background:${
    isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)"
  }`;

const participantInfoStyle =
  "display:flex;align-items:center;gap:12px;flex:1;min-width:0";

const participantMetaStyle =
  "display:flex;flex-direction:column;min-width:0;flex:1";

const participantNameStyle = (isDark: boolean) =>
  `font-size:14px;font-weight:600;color:${
    isDark ? "#f4f4f4" : "#222"
  };white-space:nowrap;overflow:hidden;text-overflow:ellipsis`;

const participantIdStyle = (isDark: boolean) =>
  `font-size:11px;font-family:monospace;color:${
    isDark ? "#9ca3af" : "#6b7280"
  };white-space:nowrap;overflow:hidden;text-overflow:ellipsis`;

const copyButtonStyle = (isDark: boolean) =>
  `background:${
    isDark ? "#2a2a2a" : "#e5e7eb"
  };border:none;cursor:pointer;padding:6px 12px;border-radius:6px;font-size:12px;font-weight:500;color:${
    isDark ? "#f4f4f4" : "#222"
  };transition:background 0.15s;flex-shrink:0`;

const popupTitleStyle = (isDark: boolean) =>
  `font-size:18px;font-weight:700;color:${
    isDark ? "#f4f4f4" : "#222"
  };margin:0;text-align:center`;

const closeButtonStyle = (isDark: boolean) =>
  `padding:8px 16px;border-radius:8px;border:none;cursor:pointer;font-size:14px;font-weight:500;background:${
    isDark ? "#2a2a2a" : "#e5e7eb"
  };color:${
    isDark ? "#f4f4f4" : "#222"
  };transition:background 0.15s;align-self:center;margin-top:8px`;

const sameCredentials = (
  a: Credentials | null,
  b: Credentials | null,
): boolean =>
  a === b || !!a && !!b && a.publicSignKey === b.publicSignKey &&
    a.privateSignKey === b.privateSignKey &&
    a.privateEncryptKey === b.privateEncryptKey;

const credentialsOrNull = (value: unknown): Credentials | null => {
  if (!value || typeof value !== "object") return null;
  const publicSignKey = Reflect.get(value, "publicSignKey");
  const privateSignKey = Reflect.get(value, "privateSignKey");
  const privateEncryptKey = Reflect.get(value, "privateEncryptKey");
  return typeof publicSignKey === "string" &&
      typeof privateSignKey === "string" &&
      typeof privateEncryptKey === "string"
    ? { publicSignKey, privateSignKey, privateEncryptKey }
    : null;
};

type ReactionMsg = DecipheredMessage & {
  type: "reaction";
  reactTo: string;
  emoji: string;
  remove?: boolean;
};

const isReaction = (m: DecipheredMessage): m is ReactionMsg =>
  m.type === "reaction";

const aggregateReactions = (
  reactions: ReactionMsg[],
  details: IdentityDetails,
) =>
(msgId: string): Reaction[] => {
  const active = new Map<string, ReactionMsg>();
  sortKey((r: ReactionMsg) => r.timestamp)(
    reactions.filter((r) => r.reactTo === msgId),
  ).forEach((r) => {
    const key = `${r.publicSignKey}:${r.emoji}`;
    if (r.remove) active.delete(key);
    else active.set(key, r);
  });
  return Array.from(active.values()).map((r) => ({
    emoji: r.emoji,
    authorId: r.publicSignKey,
    authorName: resolveName(details)(r.publicSignKey),
  }));
};

const msgToUIMessage =
  (details: IdentityDetails) =>
  (msg: DecipheredMessage): AbstracChatMessage & { _replyToId?: string } => ({
    id: msg.id,
    authorId: msg.publicSignKey,
    authorName: resolveName(details)(msg.publicSignKey),
    authorAvatar: details[msg.publicSignKey]?.avatar,
    text: msg.text,
    timestamp: msg.timestamp,
    attachments: hasAttachments(msg) ? msg.attachments : undefined,
    _replyToId: msg.type === "text" ? msg.replyTo : undefined,
  });

const msgToUIMessageWithHistory = (details: IdentityDetails) =>
(
  { msg, editHistory }: {
    msg: DecipheredMessage;
    editHistory?: EditHistoryEntry[];
  },
): AbstracChatMessage => ({
  ...msgToUIMessage(details)(msg),
  editHistory,
});

const resolveReplyTo =
  (msgMap: Map<string, AbstracChatMessage>) =>
  (msg: AbstracChatMessage & { _replyToId?: string }): AbstracChatMessage => {
    const { _replyToId, ...rest } = msg;
    if (!_replyToId) return rest;
    const target = msgMap.get(_replyToId);
    if (!target) {
      return {
        ...rest,
        replyTo: {
          id: _replyToId,
          authorId: "",
          authorName: "Unknown",
          text: "",
        },
      };
    }
    return {
      ...rest,
      replyTo: {
        id: target.id,
        authorId: target.authorId,
        authorName: target.authorName,
        text: target.text,
      },
    };
  };

type UiOverride = {
  active?: boolean;
  percentage?: number;
  text?: string;
  type?: string;
};

const latestSpinners = (
  messages: DecipheredMessage[],
  details: IdentityDetails,
  uiOverrides: Map<string, UiOverride>,
): ActiveSpinner[] => {
  const byElement = new Map<string, DecipheredMessage>();
  sortKey((x: DecipheredMessage) => x.timestamp)(
    messages.filter((m) => m.type === "spinner"),
  ).forEach((m) => {
    if (m.type === "spinner") byElement.set(m.elementId, m);
  });
  return Array.from(byElement.values())
    .filter((m) => m.type === "spinner")
    .map((m) => {
      if (m.type !== "spinner") throw new Error("unreachable");
      const override = uiOverrides.get(m.elementId);
      return {
        authorName: resolveName(details)(m.publicSignKey),
        text: m.text,
        elementId: m.elementId,
        timestamp: m.timestamp,
        active: isPast(m.timestamp, messages)
          ? false
          : (override?.active ?? m.active),
      };
    });
};

const latestProgress = (
  messages: DecipheredMessage[],
  details: IdentityDetails,
  uiOverrides: Map<string, UiOverride>,
): ActiveProgress[] => {
  const byElement = new Map<string, DecipheredMessage>();
  sortKey((x: DecipheredMessage) => x.timestamp)(
    messages.filter((m) => m.type === "progress"),
  ).forEach((m) => {
    if (m.type === "progress") byElement.set(m.elementId, m);
  });
  return Array.from(byElement.values())
    .filter((m) => m.type === "progress")
    .map((m) => {
      if (m.type !== "progress") throw new Error("unreachable");
      const override = uiOverrides.get(m.elementId);
      return {
        authorName: resolveName(details)(m.publicSignKey),
        text: m.text,
        percentage: isPast(m.timestamp, messages)
          ? 1
          : (override?.percentage ?? m.percentage),
        elementId: m.elementId,
        timestamp: m.timestamp,
      };
    });
};

const enforceMonotonic = (
  maxMap: Map<string, number>,
  entries: ActiveProgress[],
): ActiveProgress[] =>
  entries.map((entry) => {
    const monotonic = Math.max(
      maxMap.get(entry.elementId) ?? 0,
      entry.percentage,
    );
    maxMap.set(entry.elementId, monotonic);
    return { ...entry, percentage: monotonic };
  });

const messageElementIds = (messages: DecipheredMessage[]) =>
  new Set(
    messages
      .filter((m): m is DecipheredMessage & { elementId: string } =>
        "elementId" in m
      )
      .map((m) => m.elementId),
  );

type UiElement = {
  elementId: string;
  active?: boolean;
  percentage?: number;
  text?: string;
  type?: string;
  updatedAt: number;
};

const standaloneProgressEntries = (
  uiElements: UiElement[],
  knownIds: Set<string>,
): ActiveProgress[] =>
  uiElements
    .filter((el) => el.type === "progress" && !knownIds.has(el.elementId))
    .map((el) => ({
      authorName: "",
      text: el.text ?? "",
      percentage: el.percentage ?? 0,
      elementId: el.elementId,
      timestamp: el.updatedAt,
    }));

const standaloneStreamEntries = (
  streams: EphemeralStreamEvent[],
  knownIds: Set<string>,
  persistedTexts: Set<string>,
  details: IdentityDetails,
): ActiveStream[] =>
  streams
    .filter((el) => {
      if (knownIds.has(el.elementId)) return false;
      const trimmed = el.text?.trim() ?? "";
      if (trimmed !== "" && persistedTexts.has(el.text ?? "")) return false;
      return el.active !== false || trimmed !== "";
    })
    .map((el) => ({
      authorName: el.authorId ? resolveName(details)(el.authorId) : "",
      authorAvatar: el.authorId ? details[el.authorId]?.avatar : undefined,
      authorPublicKey: el.authorId,
      text: el.text ?? "",
      elementId: el.elementId,
      timestamp: el.updatedAt,
      active: el.active !== false,
    }));

const uiOverridesMap = (uiElements: UiElement[]) =>
  new Map(
    uiElements.map((el) => [el.elementId, {
      active: el.active,
      percentage: el.percentage,
      text: el.text,
      type: el.type,
    }]),
  );

const chatSwitcherContainerStyle =
  "display:flex;flex-direction:row;width:100%;height:100%;min-height:0;min-width:0;overflow:hidden;position:relative";

const sidebarContainerStyle = (isDark: boolean, customColors?: CustomColors) =>
  `width:280px;min-width:240px;max-width:320px;flex-shrink:0;display:flex;flex-direction:column;height:100%;min-height:0;background:${
    customColors?.background ?? (isDark ? "#111111" : "#f8f7f4")
  };border-right:1px solid ${
    isDark ? "#2a2a2a" : "#e5e7eb"
  };font-family:sans-serif;box-sizing:border-box`;

const sidebarHeaderStyle = (isDark: boolean) =>
  `padding:12px 14px;border-bottom:1px solid ${
    isDark ? "#222222" : "#eeeeee"
  };display:flex;flex-direction:column;gap:8px;flex-shrink:0`;

const sidebarSearchInputStyle = (
  isDark: boolean,
  customColors?: CustomColors,
) =>
  `width:100%;padding:8px 12px;border-radius:8px;font-size:13px;outline:none;border:1px solid ${
    isDark ? "#333333" : "#e5e7eb"
  };background:${
    customColors?.inputBackground ?? (isDark ? "#1a1a1a" : "#f3f4f6")
  };color:${
    customColors?.text ?? (isDark ? "#f4f4f4" : "#111827")
  };box-sizing:border-box`;

const sidebarListStyle = (scrollbarColor?: string) =>
  `flex:1;min-height:0;overflow-y:auto;padding:6px 8px;display:flex;flex-direction:column;gap:2px;${
    scrollbarColor ? `scrollbar-color:${scrollbarColor};` : ""
  }`;

const sidebarItemStyle = (
  isSelected: boolean,
  isDark: boolean,
  customColors?: CustomColors,
) =>
  `display:flex;align-items:center;gap:10px;padding:8px 10px;width:100%;text-align:left;background:${
    isSelected
      ? (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)")
      : "transparent"
  };border:none;border-radius:8px;cursor:pointer;transition:background 0.15s;box-sizing:border-box;outline:none;${
    isSelected
      ? `border-left:3px solid ${
        customColors?.primary ?? defaultPrimary(isDark)
      };`
      : ""
  }`;

const sidebarItemTitleStyle = (
  isDark: boolean,
  isSelected: boolean,
  customColors?: CustomColors,
) =>
  `font-size:13px;font-weight:${isSelected ? "600" : "500"};color:${
    customColors?.text ?? (isDark ? "#f4f4f4" : "#111827")
  };white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:1`;

const mobileBackButtonStyle = (isDark: boolean, customColors?: CustomColors) =>
  `position:absolute;top:10px;left:10px;z-index:90;display:flex;align-items:center;gap:6px;padding:6px 12px;border-radius:18px;background:${
    isDark ? "rgba(30,30,30,0.9)" : "rgba(255,255,255,0.9)"
  };color:${
    customColors?.text ?? (isDark ? "#f4f4f4" : "#111827")
  };border:1px solid ${
    isDark ? "#444" : "#e5e7eb"
  };box-shadow:0 2px 8px rgba(0,0,0,0.15);cursor:pointer;font-size:12px;font-weight:600;backdrop-filter:blur(4px)`;

const mobileFullListStyle = (isDark: boolean, customColors?: CustomColors) =>
  `display:flex;flex-direction:column;width:100%;height:100%;min-height:0;background:${
    customColors?.background ?? (isDark ? "#111111" : "#f8f7f4")
  };font-family:sans-serif;box-sizing:border-box`;

const mainChatContainerStyle =
  "display:flex;flex-direction:column;flex:1;min-width:0;min-height:0;overflow:hidden;position:relative";

const chatsIconSvg = html`
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
`;

const notFoundHtml = (customColors?: CustomColors, isDark?: boolean) =>
  html`
    <div
      style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;width:100%;color:${customColors
        ?.text ?? "#6b7280"};background:${customColors?.background ??
        (isDark ? "#111" : "#f9fafb")};font-family:sans-serif"
    >
      <div style="font-size:3rem;margin-bottom:1rem">?</div>
      <div style="font-size:1.25rem;font-weight:600;margin-bottom:0.5rem">
        Chat Not Found
      </div>
      <div style="font-size:0.9rem">This conversation does not exist.</div>
    </div>
  `;

const accessDeniedHtml = (
  onClose: (() => void) | undefined,
  customColors?: CustomColors,
  isDark?: boolean,
) =>
  html`
    <div
      style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;width:100%;color:${customColors
        ?.text ?? "#6b7280"};background:${customColors?.background ??
        (isDark ? "#111" : "#f9fafb")};font-family:sans-serif"
    >
      <div style="font-size:3rem;margin-bottom:1rem">&#128274;</div>
      <div style="font-size:1.25rem;font-weight:600;margin-bottom:0.5rem">
        Access Denied
      </div>
      <div style="font-size:0.9rem">
        You do not have the encryption keys for this conversation.
      </div>
      ${onClose
        ? html`
          <button
            type="button"
            @click="${onClose}"
            style="margin-top:1.5rem;padding:0.5rem 1rem;background:${customColors
              ?.primary ??
              "#1a1a1a"};color:white;border-radius:0.375rem;border:none;cursor:pointer;font-weight:500"
          >
            Go Back
          </button>
        `
        : nothing}
    </div>
  `;

export class ConnectedChat extends LitElement {
  static override properties = {
    credentials: { attribute: false },
    conversationId: { attribute: false },
    onClose: { attribute: false },
    darkModeOverride: { type: Boolean },
    customColors: { attribute: false },
    enableAttachments: { type: Boolean },
    enableAudioRecording: { type: Boolean },
    enableVoiceCall: { type: Boolean },
    enableChatSwitching: { type: Boolean },
    isDark: { type: Boolean },
    emptyMessage: { attribute: false },
    onChatWith: { attribute: false },
    onNewThread: { attribute: false },
    onConversationChange: { attribute: false },
  };

  declare credentials: Credentials | null;
  declare conversationId: string;
  declare onClose: (() => void) | undefined;
  declare darkModeOverride: boolean | undefined;
  declare customColors: CustomColors | undefined;
  declare enableAttachments: boolean;
  declare enableAudioRecording: boolean;
  declare enableVoiceCall: boolean;
  declare enableChatSwitching: boolean;
  declare isDark: boolean;
  declare emptyMessage: string | undefined;
  declare onChatWith: ((publicSignKey: string) => void) | undefined;
  declare onNewThread: ((conversationId: string) => void) | undefined;
  declare onConversationChange: ((conversationId: string) => void) | undefined;

  constructor() {
    super();
    this.credentials = null;
    this.conversationId = "";
    this.enableAttachments = true;
    this.enableAudioRecording = true;
    this.enableVoiceCall = false;
    this.enableChatSwitching = false;
    this.isDark = false;
  }

  private _activeConversationId = "";
  private _conversations: Conversation[] | null = null;
  private _searchQuery = "";
  private _nameCache = new Map<string, string | null>();
  private _nameUnsubs = new Map<string, () => void>();
  private _conversationsUnsub: (() => void) | null = null;
  private _mobileShowList = false;
  private _isMobile = false;
  private _responsiveUnsub: (() => void) | null = null;

  private _conversationKey: string | null = null;
  private _messages: DecipheredMessage[] | null = null;
  private _hadMessages = false;
  private _lastMessageSubscriptionKey: string | null | undefined = undefined;
  private _lastMessageCount = 0;
  private _canLoadMore = false;
  private _loadMore: () => void = () => {};
  private _typingNames: string[] = [];
  private _identityDetails: IdentityDetails = {};
  private _ephemeralStreams: EphemeralStreamEvent[] = [];
  private _uiElements: UiElement[] = [];
  private _hasAccess = true;
  private _convNotFound = false;
  private _conversationTitle = "Chat";
  private _isGroupChat = false;
  private _progressMax = new Map<string, number>();
  private _profileAuthorId: string | null = null;
  private _participants: Participant[] = [];
  private _showParticipantsPopup = false;
  private _copiedParticipantId: string | null = null;
  private _showSecretIdentityPopup = false;
  private _showImportIdentityPopup = false;
  private _qrCodeDataUrl = "";
  private _transferUrl = "";
  private _copiedTransferUrl = false;
  private _importing = false;

  private _unsubs: (() => void)[] = [];
  private _typingNotifier: ReturnType<typeof createTypingNotifier> | null =
    null;
  private _suppressTypingAuthor: ((publicSignKey: string) => void) | null =
    null;
  private _voiceCall: VoiceCallController | null = null;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.style.display = "flex";
    this.style.flexDirection = "column";
    this.style.flexGrow = "1";
    this.style.minHeight = "0";
    this.style.minWidth = "0";
    this.style.maxWidth = "100%";
    this._activeConversationId = this.conversationId;
    this.addEventListener("click", this._handleClick);
    if (this.enableChatSwitching) {
      this._responsiveUnsub = subscribeIsMobile((m) => {
        this._isMobile = m;
        this.requestUpdate();
      });
    }
    this._setupSubscriptions();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this._handleClick);
    this._teardown();
  }

  private get _effectiveDark() {
    return this.darkModeOverride !== undefined
      ? this.darkModeOverride
      : this.isDark;
  }

  override willUpdate(changed: Map<PropertyKey, unknown>) {
    const credentialsChanged = changed.has("credentials") &&
      !sameCredentials(
        credentialsOrNull(changed.get("credentials")),
        this.credentials,
      );
    const conversationIdChanged = changed.has("conversationId") &&
      changed.get("conversationId") !== this.conversationId;
    if (conversationIdChanged && this.conversationId) {
      this._activeConversationId = this.conversationId;
    }
    if (credentialsChanged || conversationIdChanged) {
      this._teardownConversation();
    }
  }

  override updated(changed: Map<string, unknown>) {
    const credentialsChanged = changed.has("credentials") &&
      !sameCredentials(
        credentialsOrNull(changed.get("credentials")),
        this.credentials,
      );
    const conversationIdChanged = changed.has("conversationId") &&
      changed.get("conversationId") !== this.conversationId;
    const chatSwitchingChanged = changed.has("enableChatSwitching") &&
      changed.get("enableChatSwitching") !== this.enableChatSwitching;
    if (chatSwitchingChanged) {
      if (this.enableChatSwitching) {
        if (!this._responsiveUnsub) {
          this._responsiveUnsub = subscribeIsMobile((m) => {
            this._isMobile = m;
            this.requestUpdate();
          });
        }
        this._setupChatListSubscriptions();
      } else {
        this._responsiveUnsub?.();
        this._responsiveUnsub = null;
        this._conversationsUnsub?.();
        this._conversationsUnsub = null;
        this._resubscribeIdentities();
      }
    }
    if (credentialsChanged || conversationIdChanged) {
      this._setupSubscriptions();
    }
  }

  private _teardownConversation() {
    this._unsubs.forEach((u) => u());
    this._unsubs = [];
    this._typingNotifier?.stop();
    this._typingNotifier = null;
    this._suppressTypingAuthor = null;
    this._voiceCall?.cleanup();
    this._voiceCall = null;
    this._messagesUnsub?.();
    this._messagesUnsub = null;
    this._progressMax.clear();
    this._hadMessages = false;
    this._lastMessageSubscriptionKey = undefined;
    this._lastMessageCount = 0;
    this._participants = [];
    this._showParticipantsPopup = false;
    this._copiedParticipantId = null;
    this._messages = null;
    this._conversationKey = null;
  }

  private _teardown() {
    this._teardownConversation();
    this._conversationsUnsub?.();
    this._conversationsUnsub = null;
    this._identityUnsub?.();
    this._identityUnsub = null;
    this._lastIdentityKeys = "";
    this._identityDetails = {};
    this._nameUnsubs.forEach((u) => u());
    this._nameUnsubs.clear();
    this._nameCache.clear();
    this._responsiveUnsub?.();
    this._responsiveUnsub = null;
  }

  private _setupSubscriptions() {
    if (!this.credentials) return;
    if (this.enableChatSwitching) {
      this._setupChatListSubscriptions();
    }
    this._setupConversationSubscriptions();
  }

  private _setupChatListSubscriptions() {
    if (!this.credentials) return;
    this._conversationsUnsub?.();
    this._conversationsUnsub = subscribeConversations(
      this.credentials.publicSignKey,
      (convs) => {
        this._conversations = convs;
        convs?.forEach((conv) => {
          const other = conv.participants.find(
            (p) => p.publicSignKey !== this.credentials!.publicSignKey,
          );
          if (other) this._subscribeName(other.publicSignKey);
        });
        this._resubscribeIdentities();
        if (
          !this._activeConversationId && convs && convs.length > 0 &&
          this.enableChatSwitching
        ) {
          this._selectConversation(convs[0].id);
        }
        this.requestUpdate();
      },
    );
  }

  private _subscribeName(publicSignKey: string) {
    if (this._nameUnsubs.has(publicSignKey)) return;
    const unsub = subscribeUserName(publicSignKey, (name) => {
      this._nameCache.set(publicSignKey, name);
      this.requestUpdate();
    });
    this._nameUnsubs.set(publicSignKey, unsub);
  }

  private _selectConversation(id: string) {
    if (this._activeConversationId === id && !this._mobileShowList) return;
    this._activeConversationId = id;
    this._mobileShowList = false;
    this.onConversationChange?.(id);
    this.dispatchEvent(
      new CustomEvent("conversation-change", {
        bubbles: true,
        composed: true,
        detail: { conversationId: id },
      }),
    );
    this._teardownConversation();
    this._setupConversationSubscriptions();
    this.requestUpdate();
  }

  private _setupConversationSubscriptions() {
    const targetId = this._activeConversationId || this.conversationId;
    if (!this.credentials || !targetId) return;

    const { publicSignKey } = this.credentials;

    this._typingNotifier = createTypingNotifier(
      targetId,
      publicSignKey,
    );

    this._voiceCall = createVoiceCall({
      conversationId: targetId,
      credentials: this.credentials,
      getConversationKey: () => this._conversationKey,
      getMessages: () => this._messages ?? [],
      onChange: () => this.requestUpdate(),
    });

    this._unsubs.push(
      accessDb().subscribeQuery(
        {
          conversations: {
            participants: {},
            $: { where: { id: targetId } },
          },
        },
        ({ data }) => {
          if (!data) return;
          const conv = data.conversations[0];
          this._convNotFound = !conv;
          this._conversationTitle = conv?.title || "Chat";
          this._isGroupChat = (conv?.participants.length ?? 0) > 2;
          this._participants = conv?.participants ?? [];
          this._resubscribeIdentities();
          this.requestUpdate();
        },
      ),
    );

    this._unsubs.push(
      accessDb().subscribeQuery(
        {
          identities: {
            $: { where: { publicSignKey } },
            keys: {
              $: { where: { conversation: targetId } },
            },
          },
        },
        ({ data }) => {
          if (!data) return;
          this._hasAccess = (data.identities?.[0]?.keys?.length ?? 0) > 0;
          this.requestUpdate();
        },
      ),
    );

    this._unsubs.push(
      subscribeConversationKey(
        targetId,
        this.credentials,
        (key) => {
          const resolved = nextConversationKey(this._conversationKey, key);
          if (resolved === this._lastMessageSubscriptionKey) return;
          this._lastMessageSubscriptionKey = resolved;
          this._conversationKey = resolved;
          this._resubscribeMessages();
          this.requestUpdate();
        },
      ),
    );

    const typingSub = subscribeTypingStates(
      targetId,
      publicSignKey,
      (names) => {
        this._typingNames = names;
        this.requestUpdate();
      },
    );
    this._unsubs.push(typingSub.unsub);
    this._suppressTypingAuthor = typingSub.suppressAuthor;

    this._unsubs.push(
      subscribeEphemeralStreams(targetId, (streams) => {
        this._ephemeralStreams = streams;
        this._resubscribeIdentities();
        this.requestUpdate();
      }),
    );

    this._unsubs.push(
      accessDb().subscribeQuery(
        {
          uiElements: {
            $: { where: { conversation: targetId } },
          },
        },
        ({ data }) => {
          this._uiElements = (data?.uiElements ?? []) as UiElement[];
          this.requestUpdate();
        },
      ),
    );
  }

  private _identityUnsub: (() => void) | null = null;
  private _lastIdentityKeys = "";
  private _messagesUnsub: (() => void) | null = null;

  private _resubscribeMessages() {
    const targetId = this._activeConversationId || this.conversationId;
    if (!targetId) return;
    this._messagesUnsub?.();
    this._messagesUnsub = subscribeDecryptedMessages(
      targetId,
      this._conversationKey,
      ({ messages, canLoadMore, loadMore }) => {
        if (!messages) return;
        if (messages.length === 0 && this._hadMessages) return;
        const messagesChanged = !this._messages ||
          this._messages.length !== messages.length ||
          this._messages.some((m, i) => m.id !== messages[i].id);
        const canLoadMoreChanged = this._canLoadMore !== canLoadMore;
        if (!messagesChanged && !canLoadMoreChanged) return;
        if (messages.length > this._lastMessageCount) {
          const latestMessage = messages[0];
          if (latestMessage) {
            this._suppressTypingAuthor?.(latestMessage.publicSignKey);
          }
        }
        this._messages = messages;
        this._lastMessageCount = messages.length;
        this._hadMessages = this._hadMessages || messages.length > 0;
        this._canLoadMore = canLoadMore;
        this._loadMore = loadMore;
        this._resubscribeIdentities();
        this._voiceCall?.handleMessages();
        this.requestUpdate();
      },
    );
  }

  private _resubscribeIdentities() {
    const keys = collectIdentityKeys({
      messages: this._messages,
      participants: this._participants,
      ephemeralStreams: this._ephemeralStreams,
      conversations: this._conversations,
      enableChatSwitching: this.enableChatSwitching,
    });
    const sorted = [...new Set(keys)].sort().join(",");
    if (sorted === this._lastIdentityKeys) return;
    this._lastIdentityKeys = sorted;
    this._identityUnsub?.();
    if (empty(keys)) {
      this._identityUnsub = null;
      return;
    }
    this._identityUnsub = subscribeIdentityDetailsMap(keys, (details) => {
      this._identityDetails = mergeIdentityDetails(
        this._identityDetails,
        details,
      );
      this.requestUpdate();
    });
  }

  private _processMessages() {
    const messages = this._messages ?? [];
    const details = this._identityDetails;
    const overrides = uiOverridesMap(this._uiElements);
    const knownIds = messageElementIds(messages);
    const messageTimestampFloor = latestTimestamp(messages);
    const reactions = messages.filter(isReaction);
    const addReactions = aggregateReactions(reactions, details);
    const withReactions = foldEdits(messages.filter(isTextOrEdit)).map(
      msgToUIMessageWithHistory(details),
    ).map((m) => {
      const r = addReactions(m.id);
      return r.length ? { ...m, reactions: r } : m;
    });
    const msgMap = new Map(withReactions.map((m) => [m.id, m]));
    const persistedTexts = new Set(withReactions.map((m) => m.text));
    return {
      chatMessages: withReactions.map(resolveReplyTo(msgMap)),
      activeSpinners: [
        ...latestSpinners(messages, details, overrides),
        ...standaloneSpinnerEntries(
          this._uiElements,
          knownIds,
          messageTimestampFloor,
        ),
      ],
      activeProgress: enforceMonotonic(this._progressMax, [
        ...latestProgress(messages, details, overrides),
        ...standaloneProgressEntries(this._uiElements, knownIds),
      ]),
      activeStreams: standaloneStreamEntries(
        this._ephemeralStreams,
        knownIds,
        persistedTexts,
        details,
      ),
    };
  }

  private _handleSend = (text: string, replyTo?: string) => {
    const targetId = this._activeConversationId || this.conversationId;
    if (!this._conversationKey || !this.credentials || !targetId) return;
    sendMessageWithKey({
      conversationKey: this._conversationKey,
      credentials: this.credentials,
      message: { type: "text", text, replyTo },
      conversation: targetId,
    }).catch((err) => console.error("Failed to send message", err));
    this._typingNotifier?.onBlurOrSend();
  };

  private _handleSendWithAttachments = async (
    text: string,
    files: File[],
    audioDuration?: number,
    replyTo?: string,
  ) => {
    const targetId = this._activeConversationId || this.conversationId;
    if (!this._conversationKey || !this.credentials || !targetId) return;
    try {
      const attachments: Attachment[] = [];
      for (const file of files) {
        const result = await uploadAttachment({
          credentials: this.credentials,
          conversationId: targetId,
          conversationKey: this._conversationKey,
          file,
          durationOverride: audioDuration,
        });
        if ("error" in result) {
          alert(`Failed to upload ${file.name || "file"}: ${result.error}`);
          return;
        }
        attachments.push(result);
      }
      await sendMessageWithKey({
        conversationKey: this._conversationKey,
        credentials: this.credentials,
        message: { type: "text", text, attachments, replyTo },
        conversation: targetId,
      });
      this._typingNotifier?.onBlurOrSend();
    } catch (err) {
      console.error("Failed to send message with attachments", err);
      alert(
        `Failed to send message: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  };

  private _handleDecryptAttachment = async (url: string) => {
    if (!this._conversationKey) throw new Error("No conversation key");
    const arrayBuffer = await downloadAttachment({
      url,
      conversationKey: this._conversationKey,
    });
    return URL.createObjectURL(new Blob([arrayBuffer]));
  };

  private _handleEdit = async (messageId: string, newText: string) => {
    const targetId = this._activeConversationId || this.conversationId;
    if (!this._conversationKey || !this.credentials || !targetId) return;
    try {
      await sendMessageWithKey({
        conversationKey: this._conversationKey,
        credentials: this.credentials,
        message: { type: "edit", editOf: messageId, text: newText },
        conversation: targetId,
      });
    } catch (err) {
      console.error("Failed to edit message", err);
    }
  };

  private _handleReact = (
    messageId: string,
    emoji: string,
    remove?: boolean,
  ) => {
    const targetId = this._activeConversationId || this.conversationId;
    if (!this._conversationKey || !this.credentials || !targetId) return;
    sendMessageWithKey({
      conversationKey: this._conversationKey,
      credentials: this.credentials,
      message: { type: "reaction", reactTo: messageId, emoji, remove },
      conversation: targetId,
    }).catch((err) => console.error("Failed to send reaction", err));
  };

  private _handleSendLocation = (
    latitude: number,
    longitude: number,
    label?: string,
  ) => {
    const targetId = this._activeConversationId || this.conversationId;
    if (!this._conversationKey || !this.credentials || !targetId) return;
    sendMessageWithKey({
      conversationKey: this._conversationKey,
      credentials: this.credentials,
      message: {
        type: "text",
        text: "",
        attachments: [{ type: "location", latitude, longitude, label }],
      },
      conversation: targetId,
    }).catch((err) => console.error("Failed to send location", err));
  };

  private _handleInputActivity = () => {
    this._typingNotifier?.onInput();
  };

  private _handleAvatarClick = (authorId: string) => {
    this._profileAuthorId = authorId;
    this.requestUpdate();
  };

  private _handleShowParticipants = () => {
    this._showParticipantsPopup = true;
    this.requestUpdate();
  };

  private _handleNewThread = async () => {
    if (!this.credentials || empty(this._participants)) return;
    const participantKeys = sort(
      unique([
        this.credentials.publicSignKey,
        ...this._participants.map((p) => p.publicSignKey),
      ]),
    );
    try {
      const res = await createConversation(accessAdminDb)(
        participantKeys,
        this._conversationTitle || "Chat",
        this.credentials,
      );
      if ("error" in res) {
        console.error("Failed to create new thread", res.error);
        return;
      }
      this._selectConversation(res.conversationId);
      this.onNewThread?.(res.conversationId);
      this.dispatchEvent(
        new CustomEvent("new-thread", {
          bubbles: true,
          composed: true,
          detail: { conversationId: res.conversationId },
        }),
      );
    } catch (err) {
      console.error("Failed to create new thread", err);
    }
  };

  private _handleCloseParticipants = () => {
    this._showParticipantsPopup = false;
    this.requestUpdate();
  };

  private _handleSecretIdentity = () => {
    if (!this.credentials) return;
    this._showSecretIdentityPopup = true;
    this._qrCodeDataUrl = "";
    this._transferUrl = "";
    this._copiedTransferUrl = false;
    this.requestUpdate();

    generateTransferUrl(this.credentials)
      .then(async (url) => {
        this._transferUrl = url;
        try {
          const QRCode = (await import("qrcode")).default;
          this._qrCodeDataUrl = await QRCode.toDataURL(url, {
            width: 184,
            margin: 1,
          });
        } catch (err) {
          console.error("Failed to generate QR code", err);
        }
        this.requestUpdate();
      })
      .catch((err) => {
        console.error("Failed to generate transfer URL", err);
      });
  };

  private _chatWithInFlight = false;

  private _handleClick = (e: MouseEvent) => {
    if (
      !this.enableChatSwitching || !this.credentials || e.button !== 0 ||
      e.ctrlKey || e.metaKey || e.shiftKey || e.altKey
    ) {
      return;
    }
    const target = e.target as HTMLElement | null;
    const anchor = target?.closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href") || anchor.href;
    if (!href) return;
    const parsed = parseChatWithUrl(href);
    if (!parsed) return;
    e.preventDefault();
    e.stopPropagation();
    this._handleChatWith(parsed.chatWith, parsed.topic);
  };

  private _handleChatWith = async (chatWith: string, topic?: string) => {
    if (!this.credentials || this._chatWithInFlight) return;
    if (this.onChatWith) {
      this.onChatWith(chatWith);
      return;
    }
    this._chatWithInFlight = true;
    try {
      let resolvedKey = chatWith;
      try {
        const res = await resolveHandle(chatWith);
        if (res && "publicSignKey" in res) {
          resolvedKey = res.publicSignKey;
        }
      } catch (_) {
        // fallback to chatWith
      }
      const myKey = this.credentials.publicSignKey;
      const participantKeys = sort(
        unique([myKey, resolvedKey]),
      );
      const existing = (this._conversations ?? []).find((c) => {
        const keys = sort(c.participants.map((p) => p.publicSignKey));
        return keys.length === participantKeys.length &&
          keys.every((k, i) => k === participantKeys[i]) &&
          (!topic || c.title === topic);
      });
      if (existing) {
        this._selectConversation(existing.id);
        return;
      }
      const title = topic || "Chat";
      const res = await createConversation(accessAdminDb)(
        participantKeys,
        title,
        this.credentials,
      );
      if ("error" in res) {
        console.error("Failed to create conversation", res.error);
        return;
      }
      this._selectConversation(res.conversationId);
      this.onNewThread?.(res.conversationId);
      this.dispatchEvent(
        new CustomEvent("new-thread", {
          bubbles: true,
          composed: true,
          detail: { conversationId: res.conversationId },
        }),
      );
    } catch (err) {
      console.error("Failed to start chat with", err);
    } finally {
      this._chatWithInFlight = false;
    }
  };

  private _handleImportIdentity = () => {
    this._showImportIdentityPopup = true;
    this._importing = false;
    this.requestUpdate();
  };

  private _handleCopyTransferUrl = async () => {
    try {
      if (!this._transferUrl || !(await copyToClipboard(this._transferUrl))) {
        return;
      }
      this._copiedTransferUrl = true;
      this.requestUpdate();
      setTimeout(() => {
        this._copiedTransferUrl = false;
        this.requestUpdate();
      }, 2000);
    } catch (err) {
      console.error("Failed to copy transfer URL", err);
    }
  };

  private _handleImportKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      this._handleImportSubmit();
    }
  };

  private _handleImportSubmit = async () => {
    const input = this.querySelector("#import-identity-input") as
      | HTMLInputElement
      | null;
    const v = input?.value?.trim();
    if (!v) {
      alert("Please paste your secret key or transfer URL");
      return;
    }
    this._importing = true;
    this.requestUpdate();
    try {
      const creds = await importIdentity(v);
      if (creds) {
        saveCredentials("aliceAndBotCredentials", creds);
        this.credentials = creds;
        this._showImportIdentityPopup = false;
        this.dispatchEvent(
          new CustomEvent("import-identity-success", {
            bubbles: true,
            composed: true,
            detail: { credentials: creds },
          }),
        );
        this._teardown();
        this._setupSubscriptions();
      } else {
        alert("Invalid secret key or transfer URL");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to import identity");
    } finally {
      this._importing = false;
      this.requestUpdate();
    }
  };

  private _handleParticipantClick = (authorId: string) => {
    this._showParticipantsPopup = false;
    this._profileAuthorId = authorId;
    this.requestUpdate();
  };

  private _handleCopyParticipant = async (e: Event, key: string) => {
    e.stopPropagation();
    try {
      await copyToClipboard(key);
      this._copiedParticipantId = key;
      this.requestUpdate();
      setTimeout(() => {
        this._copiedParticipantId = null;
        this.requestUpdate();
      }, 1500);
    } catch (err) {
      console.error("Failed to copy participant key", err);
    }
  };

  private _handleStartCall = () => {
    this._voiceCall?.startCall();
  };
  private _handleAcceptCall = () => {
    this._voiceCall?.acceptCall();
  };
  private _handleRejectCall = () => {
    this._voiceCall?.rejectCall();
  };
  private _handleEndCall = () => {
    this._voiceCall?.endCall();
  };
  private _handleToggleMute = () => {
    this._voiceCall?.toggleMute();
  };

  private _closeProfile = () => {
    this._profileAuthorId = null;
    this.requestUpdate();
  };

  private _handleSearchInput = (e: Event) => {
    this._searchQuery = (e.target as HTMLInputElement).value;
    this.requestUpdate();
  };

  private _renderConversationList(isDark: boolean) {
    if (!this._conversations) {
      return html`
        <div style="font-size:12px;color:${isDark
          ? "#888"
          : "#999"};padding:16px;text-align:center">
          Loading chats...
        </div>
      `;
    }
    const myKey = this.credentials?.publicSignKey ?? "";
    const filtered = this._searchQuery.trim()
      ? this._conversations.filter((c) => {
        const name = resolveConversationDisplayName(
          c,
          myKey,
          this._nameCache,
          this._identityDetails,
        );
        return name.toLowerCase().includes(this._searchQuery.toLowerCase());
      })
      : this._conversations;

    if (empty(filtered)) {
      return html`
        <div style="font-size:12px;color:${isDark
          ? "#888"
          : "#999"};padding:16px;text-align:center">
          No chats found
        </div>
      `;
    }

    const currentId = this._activeConversationId || this.conversationId;

    return filtered.map((conv) => {
      const isSelected = conv.id === currentId;
      const displayName = resolveConversationDisplayName(
        conv,
        myKey,
        this._nameCache,
        this._identityDetails,
      );
      const otherParticipant = conv.participants.find(
        (p) => p.publicSignKey !== myKey,
      );
      const otherKey = otherParticipant?.publicSignKey ?? conv.id;
      const baseColor = avatarColor(otherKey, isDark);
      const avatar = otherParticipant
        ? resolveParticipantAvatar(otherParticipant, this._identityDetails)
        : undefined;

      return html`
        <button
          type="button"
          style="${sidebarItemStyle(isSelected, isDark, this.customColors)}"
          @click="${() => this._selectConversation(conv.id)}"
        >
          <chat-avatar
            .image="${avatar}"
            .name="${displayName}"
            .baseColor="${baseColor}"
            .isDark="${isDark}"
          ></chat-avatar>
          <div style="${sidebarItemTitleStyle(
            isDark,
            isSelected,
            this.customColors,
          )}">
            ${displayName}
          </div>
        </button>
      `;
    });
  }

  private _renderChatContent(isDark: boolean) {
    const targetId = this._activeConversationId || this.conversationId;
    if (!targetId) {
      return html`
        <div
          style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;width:100%;color:${this
            .customColors?.text ??
            (isDark ? "#888" : "#999")};background:${this.customColors
            ?.background ??
            (isDark ? "#111" : "#f9fafb")};font-family:sans-serif"
        >
          Select a chat to view messages
        </div>
      `;
    }
    if (this._convNotFound) {
      return notFoundHtml(this.customColors, isDark);
    }
    if (!this._hasAccess) {
      return accessDeniedHtml(this.onClose, this.customColors, isDark);
    }

    const { chatMessages, activeSpinners, activeProgress, activeStreams } = this
      ._processMessages();

    return html`
      <chat-box
        .messages="${chatMessages}"
        .participants="${this._participants.map((p) => ({
          publicSignKey: p.publicSignKey,
          name: resolveParticipantName(p, this._identityDetails),
          avatar: resolveParticipantAvatar(p, this._identityDetails),
        }))}"
        .canLoadMore="${this._canLoadMore}"
        .loadMore="${this._loadMore}"
        .userId="${this.credentials?.publicSignKey ?? ""}"
        .credentials="${this.credentials}"
        .onSend="${this._handleSend}"
        .onSendWithAttachments="${this._handleSendWithAttachments}"
        .onClose="${this.onClose}"
        .title="${this._conversationTitle}"
        .typingUsers="${this._typingNames}"
        .isLoading="${!this._messages}"
        .darkModeOverride="${this.darkModeOverride}"
        .customColors="${this.customColors}"
        .onDecryptAttachment="${this._handleDecryptAttachment}"
        .enableAttachments="${this.enableAttachments}"
        .enableAudioRecording="${this.enableAudioRecording}"
        .enableVoiceCall="${this.enableVoiceCall}"
        .voiceCallState="${this._voiceCall?.getState() ?? "idle"}"
        .voiceCallDuration="${this._voiceCall?.getDuration() ?? 0}"
        .voiceCallMuted="${this._voiceCall?.getMuted() ?? false}"
        .remoteStream="${this._voiceCall?.getRemoteStream() ?? null}"
        .onStartCall="${this._handleStartCall}"
        .onAcceptCall="${this._handleAcceptCall}"
        .onRejectCall="${this._handleRejectCall}"
        .onEndCall="${this._handleEndCall}"
        .onToggleMute="${this._handleToggleMute}"
        .onEdit="${this._handleEdit}"
        .onReact="${this._handleReact}"
        .onSendLocation="${this._handleSendLocation}"
        .onInputActivity="${this._handleInputActivity}"
        .onAvatarClick="${this._handleAvatarClick}"
        .activeSpinners="${activeSpinners}"
        .activeProgress="${activeProgress}"
        .activeStreams="${activeStreams}"
        .isGroupChat="${this._isGroupChat}"
        .isDark="${isDark}"
        .emptyMessage="${this.emptyMessage}"
        @new-thread="${this._handleNewThread}"
        @show-participants="${this._handleShowParticipants}"
        @secret-identity="${this._handleSecretIdentity}"
        @import-identity="${this._handleImportIdentity}"
      ></chat-box>
    `;
  }

  override render(): TemplateResult | typeof nothing {
    if (
      !this.credentials ||
      (!this.conversationId && !this._activeConversationId &&
        !this.enableChatSwitching)
    ) {
      return nothing;
    }
    const isDark = this._effectiveDark;

    const renderPopups = () =>
      html`
        ${this._profileAuthorId
          ? html`
            <user-profile-popup
              .authorId="${this._profileAuthorId}"
              .authorName="${resolveName(this._identityDetails)(
                this._profileAuthorId,
              )}"
              .authorAvatar="${this._identityDetails[this._profileAuthorId]
                ?.avatar ?? ""}"
              .isDark="${isDark}"
              .onClose="${this._closeProfile}"
              .onChatWith="${this.onChatWith ??
                (this.enableChatSwitching ? this._handleChatWith : undefined)}"
            ></user-profile-popup>
          `
          : nothing} ${this._showParticipantsPopup
          ? html`
            <div style="${participantsOverlayStyle}" @click="${this
              ._handleCloseParticipants}">
              <div style="${participantsPopupStyle(isDark)}" @click="${(
                e: Event,
              ) => e.stopPropagation()}">
                <h3 style="${popupTitleStyle(isDark)}">Participants</h3>
                <div style="${participantsListStyle}">
                  ${this._participants.map((p) => {
                    const name = resolveParticipantName(
                      p,
                      this._identityDetails,
                    );
                    const avatar = resolveParticipantAvatar(
                      p,
                      this._identityDetails,
                    );
                    const baseColor = avatarColor(p.publicSignKey, isDark);
                    const isCopied =
                      this._copiedParticipantId === p.publicSignKey;
                    return html`
                      <div
                        style="${participantRowStyle(isDark)}"
                        @click="${() =>
                          this._handleParticipantClick(p.publicSignKey)}"
                        @mouseover="${(e: MouseEvent) => {
                          const target = e.currentTarget as HTMLElement;
                          target.style.background = isDark
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(0,0,0,0.05)";
                        }}"
                        @mouseout="${(e: MouseEvent) => {
                          const target = e.currentTarget as HTMLElement;
                          target.style.background = isDark
                            ? "rgba(255,255,255,0.02)"
                            : "rgba(0,0,0,0.02)";
                        }}"
                      >
                        <div style="${participantInfoStyle}">
                          <chat-avatar
                            .image="${avatar}"
                            .name="${name}"
                            .baseColor="${baseColor}"
                            .isDark="${isDark}"
                          ></chat-avatar>
                          <div style="${participantMetaStyle}">
                            <div style="${participantNameStyle(
                              isDark,
                            )}">${name}</div>
                            <div style="${participantIdStyle(
                              isDark,
                            )}">${compactPublicKey(p.publicSignKey)}</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          style="${copyButtonStyle(isDark)}"
                          @click="${(e: Event) =>
                            this._handleCopyParticipant(e, p.publicSignKey)}"
                        >
                          ${isCopied ? "Copied!" : "Copy ID"}
                        </button>
                      </div>
                    `;
                  })}
                </div>
                <button
                  type="button"
                  style="${closeButtonStyle(isDark)}"
                  @click="${this._handleCloseParticipants}"
                >
                  Close
                </button>
              </div>
            </div>
          `
          : nothing} ${this._showSecretIdentityPopup && this.credentials
          ? html`
            <div style="${participantsOverlayStyle}" @click="${() => {
              this._showSecretIdentityPopup = false;
              this.requestUpdate();
            }}">
              <div style="${participantsPopupStyle(isDark)}" @click="${(
                e: Event,
              ) => e.stopPropagation()}">
                <h3 style="${popupTitleStyle(isDark)}">Export Identity</h3>
                <p style="font-size:13px;opacity:0.9;text-align:center;line-height:1.4">
                  Scan this QR code with your mobile device to open Alice&Bot with your
                  export identity.
                </p>
                <div
                  style="display:flex;justify-content:center;align-items:center;width:200px;height:200px;background:#ffffff;border-radius:8px;padding:8px;margin: 0 auto;"
                >
                  ${this._qrCodeDataUrl
                    ? html`
                      <img
                        src="${this._qrCodeDataUrl}"
                        alt="Export Identity QR Code"
                        style="width:100%;height:100%"
                      />
                    `
                    : html`
                      <div style="color:#000000;font-size:14px">Generating...</div>
                    `}
                </div>
                <div
                  style="display:flex;flex-direction:column;gap:8px;width:100%;align-items:center"
                >
                  <button
                    type="button"
                    style="${copyButtonStyle(
                      isDark,
                    )};width:100%;padding:10px;border-radius:8px;cursor:pointer"
                    ?disabled="${!this._transferUrl}"
                    @click="${this._handleCopyTransferUrl}"
                  >
                    ${this._copiedTransferUrl
                      ? "Copied!"
                      : "Continue on another device"}
                  </button>
                  <button
                    type="button"
                    style="${closeButtonStyle(
                      isDark,
                    )};width:100%;padding:10px;border-radius:8px;cursor:pointer"
                    @click="${() => {
                      this._showSecretIdentityPopup = false;
                      this.requestUpdate();
                    }}"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          `
          : nothing} ${this._showImportIdentityPopup
          ? html`
            <div style="${participantsOverlayStyle}" @click="${() => {
              this._showImportIdentityPopup = false;
              this.requestUpdate();
            }}">
              <div style="${participantsPopupStyle(isDark)}" @click="${(
                e: Event,
              ) => e.stopPropagation()}">
                <h3 style="${popupTitleStyle(isDark)}">Import Identity</h3>
                <p style="font-size:13px;opacity:0.9;text-align:center;line-height:1.4">
                  Paste your secret key, credentials JSON, or transfer URL to import your
                  identity.
                </p>
                <input
                  type="text"
                  id="import-identity-input"
                  placeholder="Paste secret key or URL here"
                  style="width:100%;padding:10px;border-radius:8px;border:1px solid ${isDark
                    ? "#4b5563"
                    : "#d1d5db"};background:${isDark
                    ? "#2a2a2a"
                    : "#ffffff"};color:${isDark
                    ? "#ffffff"
                    : "#000000"};font-size:14px"
                  @keydown="${this._handleImportKeyDown}"
                />
                <div style="display:flex;gap:8px;justify-content:center;width:100%">
                  <button
                    type="button"
                    style="${closeButtonStyle(
                      isDark,
                    )};flex:1;padding:10px;border-radius:8px;cursor:pointer"
                    @click="${() => {
                      this._showImportIdentityPopup = false;
                      this.requestUpdate();
                    }}"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    id="import-identity-submit-btn"
                    style="${copyButtonStyle(
                      isDark,
                    )};flex:1;padding:10px;border-radius:8px;background:${isDark
                      ? "#3b82f6"
                      : "#2563eb"};color:#ffffff;cursor:pointer"
                    ?disabled="${this._importing}"
                    @click="${this._handleImportSubmit}"
                  >
                    ${this._importing ? "Importing..." : "Import"}
                  </button>
                </div>
              </div>
            </div>
          `
          : nothing}
      `;

    if (!this.enableChatSwitching) {
      return html`
        ${this._renderChatContent(isDark)} ${renderPopups()}
      `;
    }

    if (this._isMobile && this._mobileShowList) {
      return html`
        <div style="${mobileFullListStyle(isDark, this.customColors)}">
          <div style="${sidebarHeaderStyle(isDark)}">
            <div style="font-size:16px;font-weight:700;color:${this.customColors
              ?.text ?? (isDark ? "#fff" : "#111")}">Chats</div>
            <input
              type="text"
              placeholder="Search chats..."
              .value="${this._searchQuery}"
              @input="${this._handleSearchInput}"
              style="${sidebarSearchInputStyle(isDark, this.customColors)}"
            />
          </div>
          <div style="${sidebarListStyle(this.customColors?.scrollbarColor)}">
            ${this._renderConversationList(isDark)}
          </div>
        </div>
        ${renderPopups()}
      `;
    }

    if (this._isMobile && !this._mobileShowList) {
      return html`
        <div style="${mainChatContainerStyle}">
          <button
            type="button"
            style="${mobileBackButtonStyle(isDark, this.customColors)}"
            @click="${() => {
              this._mobileShowList = true;
              this.requestUpdate();
            }}"
            title="All Chats"
          >
            ${chatsIconSvg}
            <span>Chats</span>
          </button>
          ${this._renderChatContent(isDark)}
        </div>
        ${renderPopups()}
      `;
    }

    return html`
      <div style="${chatSwitcherContainerStyle}">
        <div style="${sidebarContainerStyle(isDark, this.customColors)}">
          <div style="${sidebarHeaderStyle(isDark)}">
            <input
              type="text"
              placeholder="Search chats..."
              .value="${this._searchQuery}"
              @input="${this._handleSearchInput}"
              style="${sidebarSearchInputStyle(isDark, this.customColors)}"
            />
          </div>
          <div style="${sidebarListStyle(this.customColors?.scrollbarColor)}">
            ${this._renderConversationList(isDark)}
          </div>
        </div>
        <div style="${mainChatContainerStyle}">
          ${this._renderChatContent(isDark)}
        </div>
      </div>
      ${renderPopups()}
    `;
  }
}

if (!customElements.get("alice-connected-chat")) {
  customElements.define("alice-connected-chat", ConnectedChat);
}
